import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, User, UserRole } from '../lib/api';

interface UserFormData {
  user_id: string;
  name: string;
  email: string;
  role: UserRole;
  password: string;
}

const ROLES: { value: UserRole; label: string; description: string }[] = [
  { value: 'investor', label: 'Investor', description: 'Read-only access to dashboard, clients, and patterns' },
  { value: 'uploader', label: 'Uploader', description: 'Can submit transcripts and review their own extractions' },
  { value: 'founder', label: 'Founder', description: 'Full access including Founder Gate validation' },
  { value: 'admin', label: 'Admin', description: 'Can manage users and system settings' },
];

// Secure random password: 16 chars with uppercase, lowercase, numbers, symbols.
// Shared by the create-user form and the admin password-reset flow.
function generateRandomPassword(): string {
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // Removed I, O to avoid confusion
  const lowercase = 'abcdefghjkmnpqrstuvwxyz'; // Removed i, l, o to avoid confusion
  const numbers = '23456789'; // Removed 0, 1 to avoid confusion
  const symbols = '!@#$%&*';
  const allChars = uppercase + lowercase + numbers + symbols;

  // Ensure at least one of each type
  let password = '';
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];

  // Fill the rest randomly
  for (let i = 4; i < 16; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function UserManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    user_id: '',
    name: '',
    email: '',
    role: 'uploader',
    password: '',
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [userToReset, setUserToReset] = useState<User | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetResultPassword, setResetResultPassword] = useState<string | null>(null);
  const [resetWasSelf, setResetWasSelf] = useState(false);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const user = await api.getCurrentUser();
      setCurrentUser(user);

      // Only admins can access this page
      if (user.role !== 'admin') {
        navigate('/');
        return;
      }

      loadUsers();
    } catch (err) {
      navigate('/');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const result = await api.listUsers();
      setUsers(result.users);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      user_id: '',
      name: '',
      email: '',
      role: 'uploader',
      password: '',
    });
    setError('');
    setShowPassword(false);
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      user_id: user.user_id,
      name: user.name,
      email: user.email || '',
      role: user.role,
      password: '', // Don't show existing password
    });
    setError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateUserId = () => {
    // Generate a simple user ID from the name
    const base = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
    const suffix = Math.random().toString(36).substring(2, 6);
    setFormData(prev => ({ ...prev, user_id: `${base}_${suffix}` }));
  };

  const generatePassword = () => {
    setFormData(prev => ({ ...prev, password: generateRandomPassword() }));
    setShowPassword(true); // Show the generated password so admin can copy it
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      if (editingUser) {
        // Update existing user
        await api.updateUser(editingUser.user_id, {
          name: formData.name,
          email: formData.email || undefined,
          role: formData.role,
        });
        setSuccess(`User "${formData.name}" updated successfully`);
      } else {
        // Create new user
        if (!formData.user_id) {
          setError('User ID is required');
          setSaving(false);
          return;
        }
        if (!formData.email) {
          setError('Email is required');
          setSaving(false);
          return;
        }
        if (!formData.password) {
          setError('Password is required for new users');
          setSaving(false);
          return;
        }
        if (formData.password.length < 12) {
          setError('Password must be at least 12 characters');
          setSaving(false);
          return;
        }

        await api.createUser({
          user_id: formData.user_id,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          password: formData.password,
        });
        setSuccess(`User "${formData.name}" created successfully`);
      }

      closeModal();
      loadUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (user: User) => {
    setUserToDelete(user);
    setError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    setError('');
  };

  const handleDelete = async () => {
    if (!userToDelete) return;

    setError('');
    setDeleting(true);

    try {
      await api.deleteUser(userToDelete.user_id);
      setSuccess(`User "${userToDelete.name}" deleted successfully`);
      closeDeleteModal();
      loadUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  const openResetModal = (user: User) => {
    setUserToReset(user);
    setResetError('');
    setResetResultPassword(null);
    setResetWasSelf(false);
    setShowResetModal(true);
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setUserToReset(null);
    setResetError('');
    setResetResultPassword(null);
    setResetting(false);
  };

  const handleConfirmReset = async () => {
    if (!userToReset) return;

    setResetError('');
    setResetting(true);

    const password = generateRandomPassword();

    try {
      await api.resetUserPassword(userToReset.user_id, password);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setResetError(message);
      setResetting(false);
      return;
    }

    // Resetting your own password revokes your current session server-side
    // (along with every other session for that user), so the admin's
    // in-progress token is now dead. Transparently re-login with the new
    // password to pick up a fresh one - otherwise the next API call would
    // 401 and bounce them to /login mid-flow.
    const isSelf = currentUser?.user_id === userToReset.user_id;
    if (isSelf && currentUser?.email) {
      try {
        await api.login(currentUser.email, password);
      } catch {
        // Reset itself succeeded even if the automatic re-login didn't;
        // the user can still sign in manually with the new password.
      }
    }

    setResetWasSelf(isSelf);
    setResetResultPassword(password);
    setResetting(false);
    setSuccess(
      `Password reset for "${userToReset.name}"` +
        (isSelf ? ' - your saved sign-in credentials were updated automatically' : '')
    );
    loadUsers();
  };

  const getRoleBadgeClass = (role: UserRole) => {
    switch (role) {
      case 'admin': return 'badge badge-info';
      case 'founder': return 'badge badge-success';
      case 'investor': return 'badge badge-warning';
      default: return 'badge';
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="page-subtitle">Manage system users and their access roles</p>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add User
        </button>
      </div>

      {error && !showModal && <div className="error-message mb-2">{error}</div>}
      {success && <div className="success-message mb-2">{success}</div>}

      {/* Stats */}
      <div className="dashboard-stats mb-2">
        <div className="stat-card">
          <div className="stat-value">{users.length}</div>
          <div className="stat-label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.role === 'admin').length}</div>
          <div className="stat-label">Admins</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.role === 'founder').length}</div>
          <div className="stat-label">Founders</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{users.filter(u => u.role === 'uploader').length}</div>
          <div className="stat-label">Uploaders</div>
        </div>
      </div>

      {/* User List */}
      {users.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <p className="text-muted">No users found</p>
            <p className="text-small text-muted">Create the first user to get started</p>
            <button className="btn btn-primary mt-1" onClick={openCreateModal}>
              + Add User
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Last Login</th>
                <th>Transcripts</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.user_id}>
                  <td>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-small text-muted">{user.email || user.user_id}</div>
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(user.role)}>
                      {user.role}
                    </span>
                  </td>
                  <td className="text-small">
                    {user.last_login_at ? (
                      <span title={new Date(user.last_login_at).toLocaleString()}>
                        {formatRelativeTime(user.last_login_at)}
                      </span>
                    ) : (
                      <span className="text-muted">Never</span>
                    )}
                  </td>
                  <td className="text-center">
                    {user.transcripts_uploaded || 0}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="btn btn-ghost btn-small"
                        onClick={() => openEditModal(user)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-ghost btn-small"
                        onClick={() => openResetModal(user)}
                      >
                        Reset Password
                      </button>
                      <button
                        className="btn btn-ghost btn-small"
                        onClick={() => openDeleteModal(user)}
                        style={{ color: 'var(--error)' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="error-message mb-2">{error}</div>}

                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                    placeholder="John Doe"
                  />
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label className="form-label">User ID *</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleInputChange}
                        className="form-input"
                        required
                        placeholder="john_doe_a1b2"
                        pattern="[a-z0-9_]+"
                        title="Lowercase letters, numbers, and underscores only"
                      />
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={generateUserId}
                        disabled={!formData.name}
                      >
                        Generate
                      </button>
                    </div>
                    <p className="form-hint">Unique identifier. Lowercase letters, numbers, underscores only.</p>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="john@example.com"
                    required
                  />
                  <p className="form-hint">Used to sign in. Must be unique across all users.</p>
                </div>

                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  <p className="form-hint">
                    {ROLES.find(r => r.value === formData.role)?.description}
                  </p>
                </div>

                {!editingUser && (
                  <div className="form-group">
                    <label className="form-label">Password *</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className="form-input"
                          required={!editingUser}
                          minLength={12}
                          placeholder="Minimum 12 characters"
                          style={{ paddingRight: '2.5rem' }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                            padding: '0.25rem',
                          }}
                          title={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? '🙈' : '👁️'}
                        </button>
                      </div>
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={generatePassword}
                      >
                        Generate
                      </button>
                    </div>
                    {formData.password && showPassword && (
                      <p className="form-hint" style={{
                        fontFamily: 'monospace',
                        backgroundColor: 'var(--bg-tertiary)',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        marginTop: '0.5rem'
                      }}>
                        {formData.password}
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(formData.password)}
                          style={{
                            marginLeft: '0.5rem',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-muted)',
                          }}
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      </p>
                    )}
                    <p className="form-hint">
                      This password will be used to sign in. Share it securely with the user.
                    </p>
                  </div>
                )}

                {editingUser && (
                  <div className="form-group">
                    <p className="text-small text-muted mb-1">
                      Generate a new password for this user. Their current password will stop working immediately.
                    </p>
                    <button
                      type="button"
                      className="btn btn-secondary btn-small"
                      onClick={() => {
                        const user = editingUser;
                        closeModal();
                        openResetModal(user);
                      }}
                    >
                      Reset Password
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>Delete User</h2>
              <button className="modal-close" onClick={closeDeleteModal}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="error-message mb-2">{error}</div>}
              <p>
                Are you sure you want to delete <strong>{userToDelete.name}</strong>?
              </p>
              <p className="text-small text-muted mt-1">
                This action cannot be undone. The user will no longer be able to log in,
                but their uploaded transcripts and other data will be preserved.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleDelete}
                disabled={deleting}
                style={{ backgroundColor: 'var(--error)', color: 'white' }}
              >
                {deleting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && userToReset && (
        <div className="modal-overlay" onClick={resetResultPassword ? undefined : closeResetModal}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button className="modal-close" onClick={closeResetModal}>&times;</button>
            </div>
            <div className="modal-body">
              {resetError && <div className="error-message mb-2">{resetError}</div>}

              {!resetResultPassword ? (
                <>
                  <p>
                    Reset the password for <strong>{userToReset.name}</strong>?
                  </p>
                  <p className="text-small text-muted mt-1">
                    A new random password will be generated and their current password will
                    stop working immediately.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-small text-muted">
                    New password for <strong>{userToReset.name}</strong>{resetWasSelf ? ' (you)' : ''}:
                  </p>
                  <p className="form-hint" style={{
                    fontFamily: 'monospace',
                    backgroundColor: 'var(--bg-tertiary)',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    marginTop: '0.5rem'
                  }}>
                    {resetResultPassword}
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(resetResultPassword)}
                      style={{
                        marginLeft: '0.5rem',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                      }}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  </p>
                  <p className="text-small mt-1" style={{ color: 'var(--error)' }}>
                    This password will not be shown again. Share it securely with the user now.
                  </p>
                  {resetWasSelf && (
                    <p className="text-small text-muted mt-1">
                      Your saved sign-in credentials were updated automatically, so you will
                      stay signed in.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              {!resetResultPassword ? (
                <>
                  <button type="button" className="btn btn-ghost" onClick={closeResetModal}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmReset}
                    disabled={resetting}
                  >
                    {resetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </>
              ) : (
                <button type="button" className="btn btn-primary" onClick={closeResetModal}>
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
